import { z } from 'zod';
import * as types from '../../ressources/glossaires/glossaire_type_d_objet_associe_a_un_rapport';

/**
 * Contrôle des objets d'un rapport modèle (en-tête, bas de page, page de garde
 * et corps), qui partent sérialisés dans json_config sans qu'aucun schéma ne
 * les ait jamais regardés.
 *
 * Les règles se limitent à ce qui empêche à coup sûr un objet d'être rendu :
 * un graphe sans graphe, une vue sans vue, une dimension illisible. Le reste
 * (texte vide, section sans nom) reste permis, faute de savoir si des rapports
 * existants en dépendent : aucun jeu de données réel n'a pu être consulté.
 *
 * Rien n'est converti : les objets partent tels quels, seule la décision
 * d'envoyer en dépend. Les erreurs sont rangées par chemin, par exemple
 * « body_page.2.0 » pour le premier objet de la troisième section du corps,
 * le chemin que FormulaireListeObjetsRapport reconstruit à l'affichage.
 */

const TYPES_CONNUS = Object.entries(types)
  .filter(([cle]) => cle !== 'objet_json')
  .map(([, valeur]) => valeur);

/** Types qui contiennent eux-mêmes une liste d'objets. */
const CONTENEURS = [types.section, types.sous_section, types.boite];

const DIMENSIONS = [
  'width',
  'height',
  'crop_x',
  'crop_y',
  'crop_width',
  'crop_height',
  'n_cols',
];

/**
 * Dimension facultative : absente ou null, elle est laissée au moteur de
 * rendu ; renseignée, elle doit se lire comme un nombre positif ou nul. Le
 * texte « 12 » est accepté, l'éditeur JSON pouvant en produire.
 */
const dimension = z.union([
  z.null(),
  z.number().min(0),
  z
    .string()
    .trim()
    .min(1)
    .refine((v) => Number.isFinite(Number(v)) && Number(v) >= 0),
]);

/** Identifiant de graphe ou de vue choisi : entier positif. */
const identifiant = z.coerce.number().int().min(1);

const verifierObjet = (objet) => {
  if (objet === null || typeof objet !== 'object' || Array.isArray(objet)) {
    return 'valeur_invalide';
  }
  if (!TYPES_CONNUS.includes(Number(objet.report_object_type))) {
    return 'type_d_objet_rapport_inconnu';
  }
  const type = Number(objet.report_object_type);

  if (type === types.graphe && !identifiant.safeParse(objet.graph_id).success) {
    return 'selectionner_un_graphe';
  }
  if (type === types.vue && !identifiant.safeParse(objet.view_id).success) {
    return 'saisir_l_identifiant_de_la_vue';
  }
  for (const cle of DIMENSIONS) {
    if (cle in objet && !dimension.safeParse(objet[cle]).success) {
      return 'dimension_invalide';
    }
  }
  if (
    CONTENEURS.includes(type) &&
    objet.objects_list !== undefined &&
    !Array.isArray(objet.objects_list)
  ) {
    return 'valeur_invalide';
  }
  return null;
};

const parcourir = (objets, chemin, erreurs) => {
  if (!Array.isArray(objets)) {
    return;
  }
  objets.forEach((objet, indice) => {
    const cheminObjet = `${chemin}.${indice}`;
    const message = verifierObjet(objet);
    if (message) {
      erreurs[cheminObjet] = message;
    }
    if (objet && Array.isArray(objet.objects_list)) {
      parcourir(objet.objects_list, cheminObjet, erreurs);
    }
  });
};

/**
 * @param {Record<string, object[]|undefined>} listesParPartie - la liste
 *   d'objets de chaque partie du rapport, indexée par nom de partie
 * @returns {{valide: boolean, erreurs: Record<string, string>}}
 */
export const validerObjetsRapport = (listesParPartie) => {
  const erreurs = {};
  for (const [partie, objets] of Object.entries(listesParPartie)) {
    parcourir(objets, partie, erreurs);
  }
  return { valide: Object.keys(erreurs).length === 0, erreurs };
};
